/**
 * Meta Pixel (Facebook/Instagram) — lightweight tracking utility.
 *
 * All calls are no-ops when VITE_META_PIXEL_ID is empty (i.e. local dev
 * without the env var set), or when the global fbq stub hasn't loaded
 * (e.g. ad blocker stripped the snippet).
 *
 * GDPR: defaults to fbq('consent', 'revoke') in initPixel(). The cookie
 * banner (CookieConsent.tsx) flips to 'grant' on accept — mirrors the
 * Google Consent Mode v2 pattern used for GA4 and Google Ads.
 *
 * Set VITE_META_PIXEL_ID in your .env to enable tracking.
 */

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
  }
}

const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID || '';

function isEnabled(): boolean {
  return META_PIXEL_ID.length > 0 && typeof window !== 'undefined';
}

/**
 * Inject the canonical Meta Pixel base code, register the pixel ID, and
 * default consent to 'revoke'. Call grantPixelConsent() from the cookie
 * banner to flip to 'grant'. Safe to call multiple times.
 */
export function initPixel(): void {
  if (!isEnabled()) return;
  if (typeof window.fbq === 'function') return; // already injected

  // Canonical Meta Pixel base code — DO NOT modify the IIFE shape, this is
  // the snippet Facebook ships and tooling (Pixel Helper, CAPI Gateway)
  // detects by exact pattern.
  /* eslint-disable @typescript-eslint/no-explicit-any */
  (function (f: any, b: any, e: string, v: string, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod
        ? n.callMethod.apply(n, arguments)
        : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    'script',
    'https://connect.facebook.net/en_US/fbevents.js',
  );
  /* eslint-enable @typescript-eslint/no-explicit-any */

  // GDPR default: deny. Pixel script loads but no cookies/IDs are set
  // until grantPixelConsent() is called from the cookie banner accept handler.
  window.fbq!('consent', 'revoke');
  window.fbq!('init', META_PIXEL_ID);
}

/**
 * GDPR: grant Pixel consent. Call from CookieConsent.accept().
 * Idempotent — safe to call on every page load to restore prior session
 * consent (read from localStorage).
 */
export function grantPixelConsent(): void {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  window.fbq('consent', 'grant');
}

/**
 * GDPR: explicitly deny Pixel consent. Call from CookieConsent.decline().
 * Default state is already revoked (set in initPixel), so this is mostly
 * defensive — useful if a user changes mind after previously granting.
 */
export function denyPixelConsent(): void {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  window.fbq('consent', 'revoke');
}

/**
 * Track a virtual page view (SPA route change). Pixel's standard event.
 */
export function trackPageView(): void {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  window.fbq('track', 'PageView');
}

/**
 * Track a Pixel standard event (CompleteRegistration, InitiateCheckout,
 * Purchase, ViewContent, etc.) or a custom event.
 *
 * Standard events map (mirrors GA4 ConversionEvents from analytics.ts):
 *   GA4 SIGNUP_COMPLETE      → Pixel CompleteRegistration
 *   GA4 CHECKOUT_STARTED     → Pixel InitiateCheckout
 *   GA4 (CheckoutSuccess)    → Pixel Purchase { value, currency }
 *   GA4 LOGIN_COMPLETE       → no Pixel standard, use 'Login' as custom
 *   GA4 FIRST_PROJECT_CREATED→ no Pixel standard, use 'CustomizeProduct'
 *
 * Standard events get richer reporting in Ads Manager + better lookalike
 * audience modeling than custom events.
 */
export function trackEvent(
  eventName: string,
  params?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  window.fbq('track', eventName, params);
}

/**
 * Pixel standard event names used in this app. Keeping them in one place
 * so refactors stay typo-safe.
 */
export const PixelEvents = {
  PAGE_VIEW: 'PageView',
  COMPLETE_REGISTRATION: 'CompleteRegistration',
  INITIATE_CHECKOUT: 'InitiateCheckout',
  PURCHASE: 'Purchase',
  VIEW_CONTENT: 'ViewContent',
} as const;
